import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './AuthModal.css';

export default function AuthModal({ onClose, defaultMode = 'signin' }) {
  const { signIn, signUp, resetPassword } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', fullName: '', phone: '', role: 'buyer' });

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setError('');
  }

  function switchMode(nextMode) {
    setMode(nextMode);
    setError('');
    setResetSent(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'reset') {
        const { error } = await resetPassword({ email: form.email });
        if (error) throw error;
        setResetSent(true);
        return;
      }

      if (mode === 'signin') {
        const { error } = await signIn({ email: form.email, password: form.password });
        if (error) throw error;
      } else {
        const { error } = await signUp({
          email: form.email,
          password: form.password,
          fullName: form.fullName,
          phone: form.phone,
          role: form.role
        });
        if (error) throw error;
      }
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>

        <div className="modal-logo">homes<span>4bid</span></div>

        {mode === 'reset' ? (
          <button
            type="button"
            onClick={() => switchMode('signin')}
            style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 20 }}
          >
            ‹ back to sign in
          </button>
        ) : (
          <div className="modal-tabs">
            <button className={mode === 'signin' ? 'active' : ''} onClick={() => switchMode('signin')}>sign in</button>
            <button className={mode === 'signup' ? 'active' : ''} onClick={() => switchMode('signup')}>create account</button>
          </div>
        )}

        {mode === 'reset' && resetSent ? (
          <div>
            <div style={{ background: 'var(--green-light)', border: '1px solid #C0DD97', color: 'var(--green)', padding: '12px 14px', borderRadius: 'var(--radius)', fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
              If an account exists for <strong>{form.email}</strong>, we've sent a password reset link. Check your inbox — the link will bring you back here to set a new password.
            </div>
            <button type="button" className="btn btn-full" onClick={() => switchMode('signin')}>back to sign in</button>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          {mode === 'reset' && (
            <div className="input-group">
              <label>email</label>
              <input
                type="email"
                placeholder="you@email.com"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                required
              />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                We'll email you a link to reset your password.
              </div>
            </div>
          )}

          {mode === 'signup' && (
            <div className="input-group">
              <label>full name</label>
              <input
                type="text"
                placeholder="Your full name"
                value={form.fullName}
                onChange={e => update('fullName', e.target.value)}
                required
              />
            </div>
          )}

          {mode === 'signup' && (
            <div className="input-group">
              <label>phone number</label>
              <input
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
                required
              />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                Used for SMS bid alerts and account verification
              </div>
            </div>
          )}

          {mode !== 'reset' && (
            <div className="input-group">
              <label>email</label>
              <input
                type="email"
                placeholder="you@email.com"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                required
              />
            </div>
          )}

          {mode !== 'reset' && (
            <div className="input-group">
              <label>password</label>
              <input
                type="password"
                placeholder={mode === 'signup' ? 'At least 8 characters' : 'Your password'}
                value={form.password}
                onChange={e => update('password', e.target.value)}
                required
                minLength={8}
              />
            </div>
          )}

          {mode === 'signin' && (
            <button
              type="button"
              onClick={() => switchMode('reset')}
              style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 14, display: 'block' }}
            >
              forgot password?
            </button>
          )}

          {mode === 'signup' && (
            <div className="input-group">
              <label>I am a</label>
              <div className="role-selector">
                {[['buyer', 'Buyer', "I want to bid on homes"],
                  ['owner', 'Owner', "I own a home"],
                  ['both', 'Both', "I\'m a buyer and owner"]
                ].map(([val, label, sub]) => (
                  <div
                    key={val}
                    className={`role-opt ${form.role === val ? 'selected' : ''}`}
                    onClick={() => update('role', val)}
                  >
                    <div className="role-label">{label}</div>
                    <div className="role-sub">{sub}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading
              ? 'please wait...'
              : mode === 'reset' ? 'send reset link' : mode === 'signin' ? 'sign in' : 'create account'}
          </button>
        </form>
        )}

        {mode === 'signup' && (
          <p className="modal-legal">
            By creating an account you agree to Homes4Bid's Terms of Use and Privacy Policy.
          </p>
        )}
      </div>
    </div>
  );
}
